/** MIDI Input panel — shows connected devices, sustain/pitch status,
 * and provides the Ableton-style MIDI learn mapping interface.
 */

import {
	Cable,
	CircleDot,
	Plug,
	Sliders,
	Trash2,
	Unplug,
	Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { UseMidiInputResult } from "../hooks/useMidiInput.ts";
import type { MidiInputDevice } from "../lib/midiInput.ts";
import type { MappableParam } from "../lib/midiMapping.ts";
import { MAPPABLE_PARAMS, PARAM_LABELS } from "../lib/midiMapping.ts";

/** Props for the MidiInputPanel component. */
export type MidiInputPanelProps = {
	readonly midi: UseMidiInputResult;
};

/** Finds which CC is mapped to a given parameter. */
function getCCForParam(
	midi: UseMidiInputResult,
	param: MappableParam,
): number | undefined {
	const mapping = midi.mappingState.mappings.find((m) => m.param === param);
	return mapping?.cc;
}

export function MidiInputPanel({ midi }: MidiInputPanelProps) {
	const [showMapping, setShowMapping] = useState(false);
	const [activityFlash, setActivityFlash] = useState(false);

	// Flash indicator on CC activity
	useEffect(() => {
		if (midi.lastActivity) {
			setActivityFlash(true);
			const timer = setTimeout(() => setActivityFlash(false), 150);
			return () => clearTimeout(timer);
		}
	}, [midi.lastActivity]);

	const handleMapClick = useCallback(
		(param: MappableParam) => {
			if (
				midi.mappingState.isLearning &&
				midi.mappingState.learningParam === param
			) {
				midi.cancelLearn();
			} else {
				midi.startLearn(param);
			}
		},
		[
			midi.mappingState.isLearning,
			midi.mappingState.learningParam,
			midi.cancelLearn,
			midi.startLearn,
		],
	);

	if (!midi.available) {
		return null;
	}

	return (
		<div className="midi-input-panel">
			<div className="midi-input-header">
				<div className="midi-input-title">
					<Cable size={14} />
					<span className="control-label" style={{ margin: 0 }}>
						MIDI Input
					</span>
					{midi.connected && (
						<span
							className={`midi-activity-dot ${activityFlash ? "flash" : ""}`}
						/>
					)}
				</div>
				<div className="midi-input-status">
					{midi.connected ? (
						<>
							<span className="midi-device-count">
								{midi.devices.length} device
								{midi.devices.length !== 1 ? "s" : ""}
							</span>
							<button
								type="button"
								className="midi-map-toggle"
								onClick={() => setShowMapping(!showMapping)}
								title="Toggle MIDI mapping"
							>
								<Sliders size={12} />
								<span>Map</span>
							</button>
						</>
					) : (
						<button
							type="button"
							className="midi-connect-btn"
							onClick={midi.connect}
							title="Connect MIDI devices"
						>
							<Plug size={12} />
							<span>Connect</span>
						</button>
					)}
				</div>
			</div>

			{/* Device list */}
			{midi.connected && midi.devices.length > 0 && (
				<div className="midi-device-list">
					{midi.devices.map((device: MidiInputDevice) => (
						<div key={device.id} className="midi-device-item">
							<CircleDot size={10} className="midi-device-icon" />
							<span className="midi-device-name">{device.name}</span>
						</div>
					))}
				</div>
			)}

			{/* Status indicators */}
			{midi.connected && (
				<div className="midi-status-row">
					{midi.sustainPedalDown && (
						<span className="midi-status-badge sustain">
							<Zap size={10} /> Sustain
						</span>
					)}
					{midi.lastNoteChannel !== null && (
						<span className="midi-status-badge">
							Ch: {midi.lastNoteChannel}
						</span>
					)}
					{midi.pitchBend !== 0 && (
						<span className="midi-status-badge pitch">
							PB: {midi.pitchBend > 0 ? "+" : ""}
							{(midi.pitchBend * 100).toFixed(0)}%
						</span>
					)}
					{midi.lastActivity && (
						<span className="midi-status-badge cc">
							CC{midi.lastActivity.cc}: {midi.lastActivity.value}
						</span>
					)}
				</div>
			)}

			{/* MIDI Mapping panel */}
			{showMapping && midi.connected && (
				<div className="midi-mapping-panel">
					<div className="midi-mapping-header">
						<span className="midi-mapping-title">MIDI CC Mapping</span>
						{midi.mappingState.isLearning && (
							<span className="midi-learn-indicator">
								<span className="midi-learn-dot" />
								Learning: {PARAM_LABELS[midi.mappingState.learningParam!]}
							</span>
						)}
					</div>
					<div className="midi-mapping-list">
						{MAPPABLE_PARAMS.map((param) => {
							const cc = getCCForParam(midi, param);
							const isLearningThis =
								midi.mappingState.isLearning &&
								midi.mappingState.learningParam === param;
							return (
								<div
									key={param}
									className={`midi-mapping-row ${isLearningThis ? "learning" : ""}`}
								>
									<span className="midi-mapping-param">
										{PARAM_LABELS[param]}
									</span>
									<span className="midi-mapping-cc">
										{cc !== undefined ? `CC ${cc}` : "—"}
									</span>
									<div className="midi-mapping-actions">
										<button
											type="button"
											className={`midi-mapping-btn ${isLearningThis ? "active" : ""}`}
											onClick={() => handleMapClick(param)}
											title={isLearningThis ? "Cancel" : "Learn"}
										>
											{isLearningThis ? (
												<Unplug size={10} />
											) : (
												<Plug size={10} />
											)}
										</button>
										{cc !== undefined && (
											<button
												type="button"
												className="midi-mapping-btn delete"
												onClick={() => midi.removeMappingForParam(param)}
												title="Remove mapping"
											>
												<Trash2 size={10} />
											</button>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
